import { createFormControl, ErrorOption, RegisterOptions } from 'react-hook-form';
import { FieldType } from '@common/entities';
import { getStatesByCountryCode } from '@common/services';
import { getValidationRules } from '@common/ui';
import { createBirthDateValidation } from '@common/validation-schemas';

import { isValidPhoneNumber } from 'components/fields/phone-number/utils';
import type { CoapplicantField, GuarantorField } from 'types/fields';

import { shouldUseUsPostalCodeBehavior } from './addressFieldMetadata';
import {
  CoapplicantCompatibilityItem,
  normalizeCoapplicantListItem,
} from './coapplicantCompatibility';

interface DraftValidationOptions {
  isGuarantor: boolean;
  isAdult: boolean;
  requiresEmail: boolean;
  isBirthDateEnabled: boolean;
  minAge: number | undefined;
  fields: CoapplicantField['configuration']['fields'] | GuarantorField['configuration']['fields'];
}

/** Validate the same row controls for saves made after their page has unmounted. */
export async function validateSecondaryApplicantDraft(
  draft: Record<string, unknown>,
  options: DraftValidationOptions,
): Promise<Record<string, ErrorOption>> {
  const { isGuarantor, isAdult, requiresEmail, isBirthDateEnabled, minAge, fields } = options;
  const rowValues = isGuarantor
    ? draft
    : normalizeCoapplicantListItem(draft as unknown as CoapplicantCompatibilityItem);
  const validationForm = createFormControl<Record<string, unknown>>({
    defaultValues: { ...rowValues },
  });
  const fieldNames: string[] = [];
  const registerField = (
    name: string,
    required: boolean,
    type = FieldType.Text,
    additionalValidation: RegisterOptions = {},
  ) => {
    fieldNames.push(name);
    validationForm.register(
      name,
      getValidationRules({
        name: `secondaryApplicant.${name}`,
        label: '',
        type,
        required,
        additionalValidation,
      }),
    );
  };
  registerField(isGuarantor ? 'guarantor_first_name' : 'coapplicant_first_name', true);
  registerField(isGuarantor ? 'guarantor_last_name' : 'coapplicant_last_name', true);
  registerField('relationship', true, FieldType.Text, {
    min: { value: 1, message: 'validation.fieldRequired' },
  });
  if (isAdult) {
    registerField(isGuarantor ? 'email' : 'coapplicant_email', requiresEmail, FieldType.Email);
    registerField(
      isGuarantor ? 'contact_phone_number' : 'coapplicant_phone_number',
      true,
      FieldType.PhoneNumber,
      { validate: (value) => isValidPhoneNumber(value) || 'validation.invalidPhone' },
    );
  }
  if (isBirthDateEnabled)
    registerField(
      isGuarantor ? 'dob' : 'coapplicant_date_of_birth',
      true,
      FieldType.Date,
      createBirthDateValidation(isAdult ? minAge : undefined, isAdult ? undefined : minAge)
        .applicant_birth_date,
    );
  if (isGuarantor) {
    const previousAddress = fields?.find((field) => field.name === 'previous_address_section');
    if (previousAddress?.enabled) {
      const countryCode = String(draft.country_code ?? '')
        .trim()
        .toUpperCase();
      ['address_line_1', 'address_line_2', 'city', 'country_code'].forEach((name) =>
        registerField(name, Boolean(previousAddress.required)),
      );
      if (!countryCode || getStatesByCountryCode(countryCode).length > 0)
        registerField('state_code', Boolean(previousAddress.required));
      registerField(
        'postal_code',
        Boolean(previousAddress.required),
        FieldType.ZipCode,
        shouldUseUsPostalCodeBehavior(countryCode) ? {} : { pattern: undefined },
      );
    }
    fields
      ?.filter(
        (field) =>
          field.enabled && ['personal_reference_1', 'personal_reference_2'].includes(field.name),
      )
      .forEach((field) => registerField(field.name, Boolean(field.required)));
  }
  await validationForm.trigger();
  const errors: Record<string, ErrorOption> = {};
  fieldNames.forEach((name) => {
    const { error } = validationForm.getFieldState(name);
    if (error) errors[name] = { type: error.type, message: error.message };
  });
  return errors;
}
